/**
 * Мапперы generated-моделей agents-сервиса → доменные структуры очереди модерации
 * (DEV-795). Все поля DTO — Optional-строки/числа, поэтому нормализуем через ?? и Number().
 */
import type { V1CompanyVerification } from '@/api/clients/agents/models/v1-company-verification'
import type { V1CompanyCandidate } from '@/api/clients/agents/models/v1-company-candidate'
import type { CompanyCandidate, CompanyVerification } from '../types/companyVerification.types'

/**
 * Нормализация уверенности в проценты. Бэк может отдавать долю (0..1) или уже
 * проценты (0..100) — единицы не зафиксированы (см. follow-up в MR/тикете), поэтому
 * долю ≤ 1 умножаем на 100, иначе округляем как есть.
 */
export function confidencePercent(raw: number): number {
  if (!raw || Number.isNaN(raw)) return 0
  return raw <= 1 ? Math.round(raw * 100) : Math.round(raw)
}

function mapCandidate(dto: V1CompanyCandidate): CompanyCandidate {
  return {
    companyId: dto.companyId ?? '',
    name: dto.name ?? '',
    website: dto.website ?? '',
    city: dto.city ?? '',
    country: dto.country ?? '',
    confidence: confidencePercent(Number(dto.confidence ?? 0)),
    sourceUrl: dto.sourceUrl ?? '',
  }
}

export function mapVerificationFromAPI(dto: V1CompanyVerification): CompanyVerification {
  return {
    id: dto.id ?? '',
    agentUserId: dto.agentUserId ?? '',
    verificationStatus: dto.verificationStatus ?? '',
    typedCompanyName: dto.typedCompanyName ?? '',
    candidates: (dto.candidates ?? []).map(mapCandidate),
    selectedCompanyId: dto.selectedCompanyId ?? '',
    responsibleId: dto.responsibleId ?? '',
    confidence: confidencePercent(Number(dto.confidence ?? 0)),
    overrideReason: dto.overrideReason ?? '',
    confirmedByAgentAt: dto.confirmedByAgentAt ?? '',
    confirmedByManagerId: dto.confirmedByManagerId ?? '',
    expiresAt: dto.expiresAt ?? '',
    createdAt: dto.createdAt ?? '',
    updatedAt: dto.updatedAt ?? '',
  }
}

/** Локация кандидата «Город, Страна» либо тире. */
export function candidateLocation(c: CompanyCandidate): string {
  return [c.city, c.country].filter(Boolean).join(', ') || '—'
}

/** Кандидат, выбранный агентом (для колонки «Выбор агента» и подсветки в модалке). */
export function selectedCandidate(v: CompanyVerification): CompanyCandidate | undefined {
  if (!v.selectedCompanyId) return undefined
  return v.candidates.find((c) => c.companyId === v.selectedCompanyId)
}

/**
 * Просрочен ли SLA: дедлайн прошёл, а заявка ещё открыта. Тот же признак, что и серверный
 * фильтр «Просроченные» (DEV-1165), поэтому финальные статусы исключаем целиком — включая
 * исторический expired, которым SLA закрывал заявки до DEV-1165.
 */
export function isSlaOverdue(v: CompanyVerification): boolean {
  if (!v.expiresAt) return false
  if (['confirmed', 'cancelled', 'expired'].includes(v.verificationStatus)) return false
  const deadline = new Date(v.expiresAt)
  if (Number.isNaN(deadline.getTime())) return false
  return deadline.getTime() < Date.now()
}
